/**
 * frontend/src/sdk/hooks/use-portfolio.ts
 *
 * TanStack Query hooks for the portfolio P&L tracking endpoints.
 */

import { useQuery } from "@tanstack/react-query";

import { apiGet } from "@/lib/api-client";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PortfolioPosition {
  id: string;
  agent_id?: string;
  market_id: string;
  token_id?: string;
  question?: string;
  side: "BUY" | "SELL";
  entry_price: number;
  exit_price?: number;
  current_price: number;
  size: number;
  shares?: number;
  entry_time?: string;
  exit_time?: string;
  exit_reason?: string;
  realized_pnl?: number;
  unrealized_pnl: number;
  pnl_pct: number;
  strategy?: string;
  signal_strength?: number;
  paper?: boolean;
  status: string;
  created_at?: string;
  source?: "live_ledger" | string;
  venue?: string;
  mode?: "paper" | "live" | string;
}

export interface PortfolioBaseline {
  active: boolean;
  baseline_at?: string;
  source?: string;
  reason_code?: string;
}

export interface PortfolioPositionsResponse {
  positions: PortfolioPosition[];
  total: number;
  generated_at: string;
  baseline?: PortfolioBaseline;
  note?: string;
}

export interface PortfolioSummary {
  total_value: number;
  unrealized_pnl: number;
  realized_pnl: number;
  total_pnl: number;
  open_positions: number;
  closed_positions: number;
  win_rate: number;
  sharpe_ratio: number;
  max_drawdown: number;
  total_invested: number;
  live_open_positions?: number;
  live_total_invested?: number;
  source?: string;
  generated_at: string;
  baseline?: PortfolioBaseline;
  note?: string;
}

export interface LiveLedgerOrder {
  order_key: string;
  correlation_id?: string;
  venue: string;
  market_id: string;
  token_id?: string;
  side: "BUY" | "SELL";
  intent_kind?: string;
  limit_price?: number;
  requested_size: number;
  requested_notional: number;
  filled_size: number;
  avg_fill_price?: number;
  mode: "live" | string;
  state: string;
  submitted_at?: string;
  acked_at?: string;
  terminal_at?: string;
  updated_at?: string;
  venue_order_id_redacted?: string;
}

export interface LiveLedgerFill {
  fill_id: number | string;
  order_key: string;
  venue: string;
  market_id: string;
  token_id?: string;
  side: "BUY" | "SELL";
  intent_kind?: string;
  fill_size: number;
  fill_price: number;
  fee_paid: number;
  mode: "live" | string;
  source?: string;
  venue_filled_at?: string;
  ingested_at?: string;
  venue_order_id_redacted?: string;
}

export interface LiveLedgerEdgeEvidence {
  correlation_id?: string;
  region?: string;
  no_order_guard_blocked: boolean;
  no_order_reason?: string;
  reason_codes: string[];
  orders_submitted_count: number;
  final_open_orders: number;
  geoblock_state?: string;
  cancel_state?: string;
  market_id_redacted?: string;
  token_id_redacted?: string;
  received_at?: string;
}

export interface LiveLedgerEdgeEvidenceResponse {
  latest: LiveLedgerEdgeEvidence | null;
  source?: string;
  note?: string;
}

export interface LiveLedgerResponse {
  orders: LiveLedgerOrder[];
  fills: LiveLedgerFill[];
  positions: PortfolioPosition[];
  edge_evidence?: LiveLedgerEdgeEvidenceResponse;
  counts: {
    orders: number;
    fills: number;
    positions: number;
  };
  generated_at: string;
  source?: string;
  baseline?: PortfolioBaseline;
  note?: string;
}

export interface PortfolioHistoryEntry {
  date: string;
  realized_pnl: number;
  cumulative_pnl: number;
  n_trades: number;
  win_count: number;
  loss_count: number;
  baseline?: boolean;
}

export interface PortfolioHistoryResponse {
  history: PortfolioHistoryEntry[];
  total_days: number;
  generated_at: string;
  baseline?: PortfolioBaseline;
  note?: string;
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function usePortfolioPositions(
  options?: { status?: string; sort?: string; limit?: number },
) {
  const params = new URLSearchParams();
  if (options?.status) params.set("status", options.status);
  if (options?.sort) params.set("sort", options.sort);
  if (options?.limit) params.set("limit", String(options.limit));

  const qs = params.toString();
  const url = `/api/portfolio/positions${qs ? `?${qs}` : ""}`;

  return useQuery({
    queryKey: ["portfolio", "positions", options],
    queryFn: () => apiGet<PortfolioPositionsResponse>(url),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

export function usePortfolioSummary() {
  return useQuery({
    queryKey: ["portfolio", "summary"],
    queryFn: () => apiGet<PortfolioSummary>("/api/portfolio/summary"),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

export function useLiveLedger(limit = 20) {
  return useQuery({
    queryKey: ["portfolio", "live-ledger", limit],
    queryFn: () =>
      apiGet<LiveLedgerResponse>(`/api/portfolio/live-ledger?limit=${limit}`),
    staleTime: 15_000,
    refetchInterval: 30_000,
  });
}

export function usePortfolioHistory(days = 90) {
  return useQuery({
    queryKey: ["portfolio", "history", days],
    queryFn: () =>
      apiGet<PortfolioHistoryResponse>(
        `/api/portfolio/history?days=${days}`,
      ),
    staleTime: 120_000,
    refetchInterval: 300_000,
  });
}
